import { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { graphql, useStaticQuery } from 'gatsby';

import ACFImage from 'types/ACFImage';

import ScheduleDemoWizard from 'components/forms/schedule-demo/schedule-demo-wizard';
import FontIcon from 'components/icons';

type ACFModalConfig = {
  advanced_large_business_form_url: string,
  advanced_medium_business_form_url: string,
  advanced_small_business_form_url: string,
  background_image: ACFImage,
  footer_image_end: ACFImage,
  footer_image_start: ACFImage,
  free_trial_promo_button: string,
  free_trial_promo_copy: string,
  free_trial_promo_title: string,
  main_copy: string,
  main_title: string,
  prod_large_business_form_url: string,
  prod_medium_business_form_url: string,
  prod_small_business_form_url: string,
  staging_large_business_form_url: string,
  staging_medium_business_form_url: string,
  staging_small_business_form_url: string,
  video_demo_button: string
  video_demo_button_2: string
  video_demo_copy: string
  video_demo_sub_title_1: string
  video_demo_sub_title_2: string
  video_demo_title: string
  video_demo_wistia_id: string
  webinar_avatar: ACFImage
  webinar_promo_button: string
  webinar_promo_copy: string
  webinar_promo_title: string
  zoom_webinar_id: string
}

type ScheduleDemoModalProps = {
  advancedUrls?: boolean,
  customHubspotMeetingUrl?: string,
  isOpen: boolean,
  onHide: () => void
}

const ScheduleDemoModal = (props:ScheduleDemoModalProps) => {
  const data = useStaticQuery(graphql`
    {
      allWordpressAcfOptions {
        edges {
          node {
            options {
              schedule_demo_modal {
                footer_image_start {
                  source_url
                }
                footer_image_end {
                  source_url
                }
                background_image {
                  source_url
                }
                main_title
                main_copy
                zoom_webinar_id
                prod_large_business_form_url
                staging_large_business_form_url
                advanced_large_business_form_url
                prod_medium_business_form_url
                staging_medium_business_form_url
                advanced_medium_business_form_url
                prod_small_business_form_url
                staging_small_business_form_url
                advanced_small_business_form_url
                webinar_avatar {
                  source_url
                }
                webinar_promo_title
                webinar_promo_copy
                webinar_promo_button
                free_trial_promo_title
                free_trial_promo_copy
                free_trial_promo_button
                video_demo_title
                video_demo_copy
                video_demo_button
                video_demo_wistia_id
                video_demo_sub_title_1
                video_demo_sub_title_2
                video_demo_button_2
              }
            }
          }
        }
      }
    }
  `);

  const modalConfig: ACFModalConfig = data.allWordpressAcfOptions.edges[0].node.options.schedule_demo_modal;

  const [hasFooterImage, setHasFooterImage] = useState(true);

  useEffect(() => {
    window.posthog?.capture('Open schedule demo options');
    const html = document.getElementsByTagName('html')[0];
    const body = document.getElementsByTagName('body')[0];

    if (!body || !html)
      return;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    return () => {
      body ? body.style.overflow = '' : undefined;
      html ? html.style.overflow = '' : undefined;
    };
  }, []);

  const largeBusinessUrl = props.advancedUrls ? modalConfig.advanced_large_business_form_url : process.env.GATSBY_DEPLOY_ENV === 'production' ? modalConfig.prod_large_business_form_url : modalConfig.staging_large_business_form_url;
  const mediumBusinessUrl = props.advancedUrls ? modalConfig.advanced_medium_business_form_url : process.env.GATSBY_DEPLOY_ENV === 'production' ? modalConfig.prod_medium_business_form_url : modalConfig.staging_medium_business_form_url;
  const smallBusinessUrl = props.advancedUrls ? modalConfig.advanced_small_business_form_url : process.env.GATSBY_DEPLOY_ENV === 'production' ? modalConfig.prod_small_business_form_url : modalConfig.staging_small_business_form_url;

  return (
    <ReactModal
      className="schedule-demo-modal--modal-container"
      isOpen={ props.isOpen }
      onRequestClose={ props.onHide }
      overlayClassName="schedule-demo-modal--overlay"
      shouldCloseOnOverlayClick={ true }
    >
      <button
        className="schedule-demo-modal--close"
        onClick={ props.onHide }
      >
        <span className="is-sr-only">Close</span>
        <FontIcon icon="close" />
      </button>
      <div
        className={ `schedule-demo-modal--main ${hasFooterImage ? 'has-footer-image' : ''}` }
        style={ { backgroundImage: hasFooterImage ? `url('${modalConfig.background_image.source_url}')` : undefined } }
      >
        <ScheduleDemoWizard
          config={ {
            backgroundImageUrl: modalConfig.background_image.source_url,
            customHubspotMeetingUrl: props.customHubspotMeetingUrl,
            formUrls: {
              largeBusiness: largeBusinessUrl,
              mediumBusiness: mediumBusinessUrl,
              smallBusiness: smallBusinessUrl,
            },
            freeTrialPromoText: {
              button: modalConfig.free_trial_promo_button,
              copy: modalConfig.free_trial_promo_copy,
              title: modalConfig.free_trial_promo_title,
            },
            mainText: {
              copy: modalConfig.main_copy,
              title: modalConfig.main_title,
            },
            videoDemoPromoText: {
              button: modalConfig.video_demo_button,
              button2: modalConfig.video_demo_button_2,
              copy: modalConfig.video_demo_copy,
              subTitle1: modalConfig.video_demo_sub_title_1,
              subTitle2: modalConfig.video_demo_sub_title_2,
              title: modalConfig.video_demo_title,
              wistiaVideoHashedId: modalConfig.video_demo_wistia_id,
            },
            webinarPromoText: {
              avatarImageUrl: modalConfig.webinar_avatar.source_url,
              button: modalConfig.webinar_promo_button,
              copy: modalConfig.webinar_promo_copy,
              title: modalConfig.webinar_promo_title,
            },
            zoomWebinarId: modalConfig.zoom_webinar_id,
          } }
          setModalFooterImageVisibility={ setHasFooterImage }
        />
        {hasFooterImage && (
          <div className="schedule-demo-modal--footer-image">
            <img src={ modalConfig.footer_image_start.source_url } alt="" aria-hidden={ true } />
          </div>
        )}
      </div>
    </ReactModal>
  );
};

export default ScheduleDemoModal;
